import { type EditFormValues } from './validation';

/**
 * Variables de `UpdateDgaTicketInput`. Todas opcionales a propósito: el backend distingue **clave
 * ausente** (no toca la columna) de clave presente, así que omitir un campo no es lo mismo que
 * mandarlo igual (AMARU-736, `.governance/wiki/dga-tickets-model.md` § semántica parcial del update).
 */
export interface UpdateTicketPayload {
  code?: string;
  openedAt?: number;
  description?: string;
}

/**
 * Los campos de `formState.dirtyFields` que este helper consulta.
 *
 * Solo los dos de texto. `openedAt` **no** está: react-hook-form recorre los valores propiedad por
 * propiedad, y un `dayjs` es un objeto — así que ahí `dirtyFields` no da un `boolean` sino un mapa de
 * las propiedades internas del objeto (`{$D, $M, $y, ...}`), que además es truthy por el solo hecho
 * de existir. Para la fecha manda la comparación del instante, ver abajo.
 */
export type EditDirtyFields = {
  code?: boolean;
  description?: boolean;
};

/**
 * Arma el input de `updateDgaTicket` con **solo los campos que el usuario tocó**.
 *
 * Mandar el formulario completo pisaría ediciones concurrentes: el prefill es una foto del ticket
 * tomada al abrir el modal, así que si otro admin corrige el detalle mientras este está abierto,
 * guardar un cambio de código con el formulario entero devolvería el detalle a su valor viejo (lost
 * update). La semántica parcial del backend existe justamente para eso — pero solo sirve si el
 * cliente la ejerce.
 *
 * Efecto secundario buscado: sin `code` ni `openedAt` en el input, corregir únicamente el detalle no
 * reescribe el espejo de DynamoDB, que es el comportamiento que la wiki documenta para la feature.
 *
 * `data` es la salida del schema zod, no lo que se tipeó: `code` ya viene normalizado
 * (`normalizeTicketCode`) y `description` ya viene con `trim()`.
 */
export const buildUpdateTicketInput = (
  data: EditFormValues,
  dirtyFields: EditDirtyFields,
  /** `openedAt` del ticket guardado, en epoch ms — el mismo valor con el que se prefilleó el form. */
  originalOpenedAt: number,
): UpdateTicketPayload => {
  const input: UpdateTicketPayload = {};

  if (dirtyFields.code) {
    input.code = data.code;
  }

  // La fecha se decide comparando el instante, no por `dirtyFields`: para react-hook-form un `dayjs`
  // es un objeto cualquiera y lo recorre propiedad por propiedad, así que su marca de "sucio" no es
  // un booleano y no distingue "el usuario abrió el picker" de "el usuario cambió la fecha". El
  // epoch sí lo distingue, y es lo único que viaja al backend.
  if (data.openedAt.valueOf() !== originalOpenedAt) {
    input.openedAt = data.openedAt.valueOf();
  }

  // Vaciar el campo es una intención válida: `''` le dice al backend que borre el detalle
  // (lo guarda como `NULL`). Por eso el fallback es `''` y no omitir la clave.
  if (dirtyFields.description) {
    input.description = data.description ?? '';
  }

  return input;
};

/** `true` si no hay nada que guardar, o sea si no hace falta llamar a la mutación. */
export const isEmptyUpdateTicketInput = (input: UpdateTicketPayload): boolean =>
  Object.keys(input).length === 0;

/**
 * `true` si el guardado necesita confirmación previa del usuario.
 *
 * Solo la corrección del **código** confirma. Es el único de los tres campos que sale del sistema: se
 * imprime en el PDF que recibe el cliente (AMARU-733) y es la clave con la que se busca el ticket en
 * la plataforma de la DGA, así que un código mal corregido desvincula el registro de su contraparte
 * real sin que nada lo delate. El detalle es contexto interno y la fecha de apertura se corrige
 * contra un dato visible en la propia DGA: ninguno de los dos justifica un paso extra en cada
 * guardado (D-007).
 *
 * Se decide sobre el input ya armado y no sobre `dirtyFields`: lo que importa es si `code` **viaja**,
 * que es exactamente la condición con la que el backend decide reescribir el espejo de DynamoDB. Un
 * campo tocado que no cambió nada no llega hasta acá — lo filtra `buildUpdateTicketInput`.
 *
 * Tipada como type guard para que el llamador pueda mostrar el valor nuevo en el diff sin un `!`.
 */
export const needsCodeChangeConfirmation = (
  input: UpdateTicketPayload,
): input is UpdateTicketPayload & { code: string } => input.code !== undefined;
